// MA-P0-2 — pure skill-tree status fold extracted from SkillTree.tsx so the
// fold logic is testable without React deps.
//
// THE BUG THIS FIXES (all changes live here):
//
// (a) PREREQ FOLD — the original fold treated only `mastered` and `age_covered`
//     as chain-alive. A `secure` prerequisite therefore set prevMastered=false
//     and all downstream unattempted skills fell through to `locked`/"Ahead",
//     leaving a dead zone with no "Now" for a 6.0yo child whose 5.5-6.0 prereq
//     was secured. Fix: treat `secure` as chain-alive too.
//
// (b) BADGE GAP — the `next` (frontier) status had no UI badge in SkillTree.tsx.
//     After (a) is fixed, the frontier skill gets `next` but renders silently.
//     Fix: the new `computeStrandStatuses` exposes `Status` so the JSX can add
//     a "Start here" badge for `next` (done in SkillTree.tsx).
//
// (c) MA-P0-3 PRECEDENCE — `practicing` was checked BEFORE `age_covered`, so a
//     below-band skill the child had attempted read "Now" while un-attempted
//     peers read "For younger ages" — inconsistent and frontier-pinning.
//     Fix: check `age_covered` before `practicing`.
//     `mastered`/`secure` still take priority (they remain first) so a child
//     who has actually mastered a below-band skill still sees a checkmark.
//
// CALIBRATION NOTE: the `secure` inclusion is intentional — a secured prereq
// IS strong evidence the child is ready for the next skill. Not including
// `practicing` is also intentional: a child actively practicing a skill is
// that chain's current "Now"; its dependents stay locked. (MA-82 slice 3: the
// fold is per prerequisite chain — see computeStrandStatuses.)

import { masteryTier } from "./masteryTiers";
import { bandRank } from "@/engine/ageBand.ts";

/** MA-841 — the eight tree statuses as a runtime list, so a test can pin the
 *  status → word mapping per member and go red on a new member. `Status` is
 *  derived from it (same eight tokens, same order as before). */
export const TREE_STATUSES = [
  "mastered",
  "secure",
  "not_yet",
  "practicing",
  "age_covered",
  "next",
  "locked",
  "suppressed",
] as const;
export type Status = (typeof TREE_STATUSES)[number];

/** Minimal shape `statusFor` needs — satisfied by SkillNode (MicroSkill + mastery). */
export type SkillStatusNode = {
  id: string;
  prerequisites: readonly string[];
  /** null = no attempt rows for this skill (useSkillGraph's contract). A
   *  non-null record means the child has attempted it (MA-85 rule (a));
   *  `attempts_count`, when present, must be > 0 for that to hold. */
  mastery: { mastery_score: number; attempts_count?: number } | null;
};

/** MA-85 rule (a): the child has at least one attempt on this skill.
 *  useSkillGraph gives `mastery: null` exactly when a skill has no attempt
 *  rows; an explicit `attempts_count: 0` is treated as not attempted. */
export function hasAttempted(node: SkillStatusNode): boolean {
  if (node.mastery == null) return false;
  const n = node.mastery.attempts_count;
  return n === undefined || n > 0;
}

/** Map a skill node to its display status given chain-alive state + age-covered set. */
export function statusFor(
  node: SkillStatusNode,
  prevMastered: boolean,
  ageCovered: Set<string>,
  aboveBand: Set<string> = new Set(),
  suppressed: Set<string> = new Set(),
): Status {
  const tier = masteryTier(node.mastery?.mastery_score);
  if (tier === "mastered") return "mastered";
  if (tier === "secure") return "secure";
  // MA-85 rule (a) (ADR-107): a below-band skill the child has ATTEMPTED but
  // not secured reads "Not yet", not "For younger ages" — the cover is
  // band-only (TREE_AGE_COVER_GAP = 0), so without this an untouched skill
  // and a tried-and-not-yet-right one carried the same chip. "For younger
  // ages" stays only on below-band skills never attempted. Chain-alive.
  if (ageCovered.has(node.id)) return hasAttempted(node) ? "not_yet" : "age_covered";
  // MA-89 (ADR-052) — a parent-removed served "Now". Checked AFTER real
  // mastery/secure/age_covered (a skill the child organically masters
  // elsewhere still shows "Mastered" even if it was once suppressed) but
  // BEFORE the above-band lock and practicing/next, so suppression overrides
  // both — a suppressed skill never reads "Now" or "Ahead", only "Removed".
  if (suppressed.has(node.id)) return "suppressed";
  // MA-91 (ADR-042) — TREE-SIDE BAND CEILING (lifts on on-band exhaustion).
  // A skill whose content band sits STRICTLY ABOVE the child's age band must not
  // read "Now" *while the child still has on-band content to do*. Without this an
  // above-band skill carrying residual `practicing` mastery (e.g. ms.math.20 for
  // a 6.0yo, practiced under pre-MA-86 code) fell through to "practicing" and
  // SkillTree rendered it as a current focus — permanent dead state, since
  // MA-86's serve cap means it is never re-served to refresh that score.
  //
  // CONDITIONAL on `!prevMastered`: in band-rank-sorted order every below-band
  // (age_covered) and on-band skill precedes the above-band ones, and only
  // mastered/secure/age_covered keep the chain alive. So `prevMastered` is true
  // at an above-band node IFF every on-band skill is already mastered/secure —
  // (MA-82 slice 3: computeStrandStatuses still passes exactly that walk-prefix
  // flag for above-band nodes; the per-chain rule applies at the current band)
  // i.e. on-band content is EXHAUSTED and advancing is legitimate (the same
  // condition under which MA-86's serve cap lifts). In that case we do NOT lock:
  // the above-band skill becomes the real frontier ("next") so the tree agrees
  // with the serve. We lock ONLY when on-band work remains (`!prevMastered`),
  // demoting the residual-practicing above-band skill from "Now" to "Ahead".
  // This also keeps `checkEngine.treeCoherence` honest: a fully-mastered on-band
  // strand still surfaces exactly one (above-band) frontier rather than a
  // locked-chip-with-no-frontier FAIL.
  //
  // mastered/secure are checked first, so a GENUINELY mastered above-band skill
  // still shows "Mastered" — real acceleration is never hidden. The set defaults
  // EMPTY (backward-compatible: age-agnostic callers/tests unchanged); SkillTree
  // passes ageAboveBandSkillIds(studentAge).
  if (aboveBand.has(node.id) && !prevMastered) return "locked";
  if (tier === "practicing") return "practicing";
  if (prevMastered || node.prerequisites.length === 0) return "next";
  return "locked";
}

/** The chain-alive ("resolved") statuses. They come from tier, age cover,
 *  a parent removal, or (MA-85) the prerequisite closure of a secured skill —
 *  never from the fold's own walk (`next`/`locked`). */
const CHAIN_ALIVE: ReadonlySet<Status> = new Set<Status>([
  "mastered",
  "secure",
  "not_yet",
  "age_covered",
  "suppressed",
]);

/** MA-85 — options for `computeStrandStatuses`. */
export type StrandFoldOptions = {
  /** Rule (b), default true: a transitive prerequisite of a secure/mastered
   *  skill that is not itself secure/mastered and not below the child's band
   *  reads `not_yet` (never `locked`, never "Now"); a below-band one keeps
   *  its cover chip (operator ruling S-1). `false` ONLY for the serve ranker's `treeLockedSkillIds`
   *  (progression.ts), which must keep its pre-MA-85 locked set: MA-85 is a
   *  tree-only slice and must not move which skill the mission picks. */
  notYetForSecuredPrereqs?: boolean;
  /** MA-1226 slice 2 (operator D-1104-21: evidence beats age): the child's
   *  diagnostic evidence floor skill ids (mission.placementFloorSkillIds, the
   *  MA-775 evidence floors /play serves while open). Display only: such a
   *  skill reads its strand's Now (`practicing` at that tier, else `next`) in
   *  place of `locked`, "For younger ages" or a cover "Not yet", unless it is
   *  secure/mastered, parent-removed, or under a secured skill (MA-85 rule (b):
   *  then it is closed). D-1104-30a: the open floor is then the strand's only
   *  Now; other would-be Now rows read the below-level status (in the floor's
   *  prerequisite closure or below its curriculum position) or Ahead (above
   *  it). The fold itself is unchanged. SkillTree passes only floors /play
   *  actually serves (the open floors). Omitted by the serve ranker's
   *  treeLockedSkillIds. */
  evidenceFloorIds?: ReadonlySet<string> | readonly string[];
  /** MA-1226 slice 2 (D-1104-31a; operator D-1104-41): the curriculum
   *  position comparator for the D-1104-30a rule, REQUIRED whenever
   *  `evidenceFloorIds` is non-empty. Callers pass placementFloor's
   *  `atOrAboveFloorPosition`. It is injected, not imported, because this
   *  module is imported by e2e specs and must stay JSON-free at module scope
   *  (Playwright's TS loader drops JSON import attributes; placementFloor
   *  imports microSkills.json). */
  atOrAboveFloorPosition?: FloorPositionCompare;
};

/** MA-1226 slice 2 — is `skillId` at or above `floorSkillId`'s curriculum
 *  position in `strand`? (placementFloor.atOrAboveFloorPosition's shape.) */
export type FloorPositionCompare = (strand: "reading" | "math", skillId: string, floorSkillId: string) => boolean;

/**
 * Run the prerequisite fold over one strand, producing display statuses.
 * Output order = input order (`sortedNodes`, the display order).
 *
 * Chain-alive statuses: `mastered`, `secure`, `age_covered`, `suppressed`.
 * MA-P0-2: `secure` is chain-alive (it was not, which caused the dead-zone
 * bug). MA-89 (ADR-052): `suppressed` is chain-alive too, so a parent-removed
 * "Now" advances the fold instead of leaving a hole. That is a DISPLAY-fold
 * decision only: REAL prerequisite satisfaction (`prerequisitesSatisfied`,
 * progression.ts) reads `states[id].masteryBand`, never `Status`.
 *
 * MA-82 slice 3 (ADR-041 §2, docs/build/specs/MA-82-tree-fold-per-chain.md):
 * the fold is PER CHAIN. It used to be one linear walk with a single
 * `prevMastered` flag, so the first unresolved skill took "Now" and every
 * later skill read "Ahead": a second prerequisite chain's legitimate frontier
 * was collapsed, and a same-band declaration reorder could move the "Now"
 * onto a dependent above its own prerequisite. Now:
 *   1. The walk follows a stable TOPOLOGICAL order of the in-strand
 *      prerequisite edges (input position breaks ties; on the real manifest
 *      the band-sorted input already is topological, prereqChains.test A2).
 *      `prefixAlive` = every node before this one in that walk is resolved
 *      (the old `prevMastered`).
 *   2. Above-band nodes keep MA-91 exactly: `locked` while on-band work
 *      remains; once the strand is exhausted, the first unresolved one is the
 *      strand's single "Now" (the live-QA judge FAILs an above-band "Now"
 *      beside any other).
 *   3. At the strand's CURRENT BAND (the lowest band holding an unresolved,
 *      not-above-band node; the child's band whenever the caller passes the
 *      age sets) the ANCESTOR rule decides: `next` iff every transitive
 *      prerequisite is resolved. Prerequisite-incomparable skills can then
 *      both read "Now", one per chain, which is what ADR-041 §2 decided.
 *   4. Any other node (only when the caller omits the age sets) keeps the
 *      old linear rule, including the root rule.
 *   5. A `next` that is a transitive prerequisite of a `practicing` chip
 *      reads `locked`: the practicing skill is that chain's "Now".
 * `mastered`/`secure`/`age_covered`/`suppressed`/`practicing` come from
 * `statusFor` exactly as before; the fold decides only `next` vs `locked`.
 *
 * MA-85 (ADR-107): `not_yet` comes from two places. (a) `statusFor`: a
 * below-band skill with attempts that is not secure/mastered. (b) here: every
 * transitive in-strand prerequisite of a secure/mastered skill that is not
 * itself secure/mastered AND not below the child's band — the rule of
 * `securePrereqClosureSkillIds` (parentPriors.ts), which the serve walk uses
 * to treat those skills as done, minus the age cover (operator ruling S-1:
 * a covered skill under a secured one keeps "For younger ages" / rule (a));
 * the manifest has no cross-strand prerequisite edges, so the in-strand
 * closure is the whole closure (ma85TreeChipSemantics.test.mjs pins the
 * equality). Precedence: mastered > secure > not_yet > (Now / For younger
 * ages / Removed / Ahead). `not_yet` is chain-alive, so a skill under a
 * secure/mastered chip is never "Ahead" and never carries a lock.
 */
export function computeStrandStatuses<T extends SkillStatusNode & { ageBand?: string }>(
  sortedNodes: T[],
  ageCovered: Set<string>,
  aboveBand: Set<string> = new Set(),
  suppressed: Set<string> = new Set(),
  options: StrandFoldOptions = {},
): Array<{ node: T; status: Status }> {
  const n = sortedNodes.length;
  // First occurrence wins for a duplicated id (prereqFoldOrderViolations' rule).
  const indexById = new Map<string, number>();
  sortedNodes.forEach((node, i) => {
    if (!indexById.has(node.id)) indexById.set(node.id, i);
  });
  const prereqIdx: number[][] = sortedNodes.map((node) => [
    ...new Set(node.prerequisites.filter((p) => indexById.has(p)).map((p) => indexById.get(p) as number)),
  ]);
  const allPrereqsInStrand = sortedNodes.map((node) => node.prerequisites.every((p) => indexById.has(p)));

  // 1. Stable topological walk (Kahn, smallest input position first). Nodes
  //    left over by a cycle (a broken manifest) follow in input order: the
  //    fold never throws or hangs in the render path.
  const indegree = prereqIdx.map((ps) => ps.length);
  const dependents: number[][] = sortedNodes.map(() => []);
  prereqIdx.forEach((ps, i) => ps.forEach((p) => dependents[p].push(i)));
  const walk: number[] = [];
  const walked = new Array<boolean>(n).fill(false);
  const ready: number[] = [];
  for (let i = 0; i < n; i++) if (indegree[i] === 0) ready.push(i);
  while (ready.length > 0) {
    ready.sort((a, b) => a - b);
    const i = ready.shift() as number;
    walked[i] = true;
    walk.push(i);
    for (const d of dependents[i]) if (--indegree[d] === 0) ready.push(d);
  }
  for (let i = 0; i < n; i++) if (!walked[i]) walk.push(i);

  // Transitive in-strand prerequisites (cycle-safe: a node on a cycle lists
  // itself, so it can never read as having every prerequisite resolved).
  const ancestors: Set<number>[] = sortedNodes.map((_, i) => {
    const seen = new Set<number>();
    const stack = [...prereqIdx[i]];
    while (stack.length > 0) {
      const p = stack.pop() as number;
      if (seen.has(p)) continue;
      seen.add(p);
      stack.push(...prereqIdx[p]);
    }
    return seen;
  });

  // The resolved statuses do not depend on the walk (statusFor resolves
  // tier, cover and suppression before it ever reads `prevMastered`).
  const fixed = sortedNodes.map((node) => statusFor(node, false, ageCovered, new Set(), suppressed));
  // MA-85 rule (b): the secured skills' prerequisite closure reads not_yet —
  // EXCEPT below the child's band (operator ruling S-1, 2026-10-05): a
  // covered skill keeps what statusFor gave it ("For younger ages" when never
  // attempted, rule (a)'s "Not yet" when tried and not secured). The cover is
  // already chain-alive and the serve walk already masks it as done, so
  // calling an untouched early skill "Not yet" under a later secured one
  // claimed a gap nothing had observed.
  if (options.notYetForSecuredPrereqs !== false) {
    const underSecured = new Set<number>();
    fixed.forEach((s, j) => {
      if (s === "mastered" || s === "secure") for (const a of ancestors[j]) underSecured.add(a);
    });
    for (const i of underSecured) {
      if (fixed[i] === "mastered" || fixed[i] === "secure" || ageCovered.has(sortedNodes[i].id)) continue;
      fixed[i] = "not_yet";
    }
  }
  const alive = fixed.map((s) => CHAIN_ALIVE.has(s));
  const prefixAlive = new Array<boolean>(n);
  let prefix = true;
  for (const i of walk) {
    prefixAlive[i] = prefix;
    if (!alive[i]) prefix = false;
  }
  const ancestorsResolved = (i: number) => [...ancestors[i]].every((a) => alive[a]);

  // 3. The strand's current band: the lowest band holding an unresolved,
  //    not-above-band node.
  let currentBand = Number.POSITIVE_INFINITY;
  sortedNodes.forEach((node, i) => {
    if (!alive[i] && !aboveBand.has(node.id)) currentBand = Math.min(currentBand, ageBandRank(node.ageBand ?? ""));
  });

  const statuses: Status[] = sortedNodes.map((node, i) => {
    if (alive[i]) return fixed[i];
    // 2. MA-91, above band: the old linear gate (plus the prerequisite check,
    //    a no-op in a topological walk).
    if (aboveBand.has(node.id)) {
      return statusFor(node, prefixAlive[i] && ancestorsResolved(i), ageCovered, aboveBand, suppressed);
    }
    // 3. At the current band: the ANCESTOR rule. A prerequisite missing from
    //    the strand cannot be checked, so such a node also needs the old
    //    linear condition.
    if (ageBandRank(node.ageBand ?? "") === currentBand) {
      const gate = ancestorsResolved(i) && (allPrereqsInStrand[i] || prefixAlive[i]);
      return statusFor(node, gate, ageCovered, aboveBand, suppressed);
    }
    // 4. Elsewhere (no age sets supplied): the old linear rule.
    return statusFor(node, prefixAlive[i] && ancestorsResolved(i), ageCovered, aboveBand, suppressed);
  });

  // 5. One "Now" per chain beside a practicing chip.
  const underPracticing = new Set<number>();
  statuses.forEach((s, i) => {
    if (s === "practicing") for (const a of ancestors[i]) underPracticing.add(a);
  });
  const floorIds = new Set(options.evidenceFloorIds ?? []);
  const atOrAboveFloorPosition = options.atOrAboveFloorPosition;
  if (floorIds.size > 0 && typeof atOrAboveFloorPosition !== "function") {
    // Fail loud: without the comparator the D-1104-30a rule cannot place a row.
    throw new Error("computeStrandStatuses: evidenceFloorIds needs options.atOrAboveFloorPosition (MA-1226, D-1104-41)");
  }
  const underSecuredFloor = new Set<number>();
  if (floorIds.size > 0) {
    fixed.forEach((s, j) => {
      if (s === "mastered" || s === "secure") for (const a of ancestors[j]) underSecuredFloor.add(a);
    });
  }
  const folded: Status[] = statuses.map((st, i) => (st === "next" && underPracticing.has(i) ? "locked" : st));
  // MA-1226 slice 2: an open evidence floor reads its strand's Now.
  const openFloors = sortedNodes
    .map((node, i) => i)
    .filter((i) => floorIds.has(sortedNodes[i].id) && !underSecuredFloor.has(i)
      && (folded[i] === "locked" || folded[i] === "age_covered" || folded[i] === "not_yet" || folded[i] === "next" || folded[i] === "practicing"));
  const out: Status[] = [...folded];
  for (const f of openFloors) {
    out[f] = masteryTier(sortedNodes[f].mastery?.mastery_score) === "practicing" ? "practicing" : "next";
    // MA-1226 slice 2 (operator D-1104-30a): while the floor is open it is the
    // strand's ONLY Now. Every other row that would read Now is shown with a
    // status master already has: a row in the floor's prerequisite closure or
    // strictly below the floor's curriculum position (placementFloor.isBelowFloor's
    // ordinal, the MA-837 one-floor rule) is below the child's current level,
    // so it reads like a below-level row (statusFor rule (a): "Not yet" when
    // attempted, else the below-the-current-level dot), never locked; any other
    // Now row is above the placement and reads "Ahead" (locked, "above the
    // current level"). Display only; no other status moves.
    const fid = sortedNodes[f].id;
    const strand = fid.startsWith("ms.math.") ? "math" : "reading";
    for (let j = 0; j < sortedNodes.length; j++) {
      if (j === f || openFloors.includes(j) || (out[j] !== "next" && out[j] !== "practicing")) continue;
      // D-1104-31a: the same position rule as the serve side (legacy ordinal,
      // else the tree's band-then-declaration order); a row is never skipped.
      const below = ancestors[f].has(j) || !atOrAboveFloorPosition!(strand, sortedNodes[j].id, fid);
      out[j] = below ? (hasAttempted(sortedNodes[j]) ? "not_yet" : "age_covered") : "locked";
    }
  }
  return sortedNodes.map((node, i) => ({ node, status: out[i] }));
}


// ── MA-93 (ADR-043) — ONE status vocabulary shared by SkillTree + SkillDetail ──
//
// The 4-word parent vocabulary (MA-62/ADR-035) used to live ONLY as inline chip
// strings in SkillTree.tsx, so SkillDetail (band-agnostic) drifted and showed a
// different word for the same skill. These are now the SINGLE definition site,
// consumed by both screens, so the two surfaces cannot disagree again.

/** Band-order rank; unknown bands sort LAST (never crash). Moved here from
 *  SkillTree so tree + detail sort a strand identically.
 *
 *  MA-79 (ADR-106 Decision 4, Step 2): re-pointed onto the canonical
 *  `bandRank` (src/engine/ageBand.ts) — no second rank implementation. The
 *  unknown-band value changes from Number.MAX_SAFE_INTEGER to
 *  AGE_BAND_ORDER.length; every consumer uses it only as a subtraction
 *  comparator (sortStrandByAgeBand, useJourneyView's strand sort,
 *  journeyView.orderKey), and both values sort every unknown band after
 *  every known band, so the order is unchanged (MA-357 (a) golden). */
export function ageBandRank(band: string): number {
  return bandRank(band);
}

/** MA-864 (RED r1 M2) — the tree's strands in display order, and its rule
 *  for drawing one: SkillTree hides a strand whose fold has no rows (the
 *  manifest has no writing skills today). The parent "Skills mastered over
 *  time" card draws a line only for a strand this returns true for, so it
 *  never shows a strand the tree does not. */
export const TREE_STRANDS = ["math", "reading", "writing"] as const;
export function treeRendersStrand(strandRows: readonly unknown[]): boolean {
  return strandRows.length > 0;
}

/** MA-79 (ADR-106 Decision 4, Step 1) — THE strand order, ONE definition.
 *  Stable sort by age-band rank ascending, tiebreak on the node's index in
 *  the input array (declaration/prerequisite order). Used by SkillTree.tsx's
 *  strandRows, by statusForSkillInStrand (SkillDetail) and by the MA-357 (a)
 *  golden, so the screen's order and the detail page's order cannot drift.
 *  Returns a new array; the input is not mutated. */
export function sortStrandByAgeBand<T extends { ageBand: string }>(nodes: readonly T[]): T[] {
  return nodes
    .map((n, i) => ({ n, i }))
    .sort((a, b) => ageBandRank(a.n.ageBand) - ageBandRank(b.n.ageBand) || a.i - b.i)
    .map((x) => x.n);
}

/** MA-841 — the ONE parent progress scale (operator decision 2026-10-07,
 *  D-1008-2/3): five words, one scale, shown in one place (the row's
 *  right-hand pill) on the tree and as SkillDetail's headline. */
export const PROGRESS_WORDS = ["Mastered", "Almost there", "Practising", "Not started", "Later"] as const;
export type ProgressWord = (typeof PROGRESS_WORDS)[number];

/** The Status → word for a row the child HAS attempted and that is not the
 *  served Now (the base column of `progressWordFor`). Typed
 *  `Record<Status, ProgressWord>`: a new status cannot compile without a
 *  word on the scale. History: MA-296 split `secure` from `mastered`
 *  (ADR-093); MA-85 / ADR-107 renamed `secure` "Almost mastered" and added
 *  `not_yet`. MA-841 folds the position words onto the progress scale
 *  (operator D-1008-2): Now and Not yet → Practising; For younger ages →
 *  Not started; Ahead → Later; a skill the parent took out of today's
 *  mission (`suppressed`) → Later (D-1008-5). The internal tokens are
 *  unchanged (live-QA, scenarios, schema read them). */
export const STATUS_CHIP_LABEL: Record<Status, ProgressWord> = {
  mastered: "Mastered",
  secure: "Almost there",
  not_yet: "Practising",
  practicing: "Practising",
  next: "Practising",
  age_covered: "Not started",
  locked: "Later",
  suppressed: "Later",
};

/** MA-841 — THE word a row shows: a total function of (status, attempted,
 *  isServedNow). Operator D-1008-5/6:
 *   - mastered / secure keep their word (both imply attempts);
 *   - a skill the parent took out of today's mission (`suppressed`) reads
 *     Later, whatever its attempts;
 *   - the served Now (the row today's mission serves) reads Practising, even
 *     with zero attempts — so exactly one Practising row is the served Now on
 *     a fresh tree;
 *   - any other row with ZERO attempts reads Not started, whatever its
 *     status (the item text: "Not started (never attempted, any band)");
 *   - otherwise the attempted word, STATUS_CHIP_LABEL[status] (an attempted
 *     age_covered, unreachable via statusFor, reads Practising). */
export function progressWordFor(status: Status, attempted: boolean, isServedNow: boolean): ProgressWord {
  if (status === "mastered" || status === "secure" || status === "suppressed") return STATUS_CHIP_LABEL[status];
  if (isServedNow) return "Practising";
  if (!attempted) return "Not started";
  // An attempted below-band skill is `not_yet` by construction (statusFor
  // rule (a)); should an attempted `age_covered` ever reach here it maps by
  // its progress like not_yet (D-1008-2), never "Not started".
  if (status === "age_covered") return "Practising";
  return STATUS_CHIP_LABEL[status];
}

/** MA-841 — the check glyph belongs to Mastered only (an "Almost there"
 *  check read as done). StatusNodeIcon draws a check iff this is true. */
export function statusShowsCheck(status: Status): boolean {
  return status === "mastered";
}

/** MA-841 — the status-specific half of SkillDetail's gloss, worded so it
 *  reads true after ANY of the words `progressWordFor` can give that status
 *  (e.g. "Not started — above where they are now…" and "Later — above where
 *  they are now…"). The `locked` line does not claim zero attempts, so the
 *  factual "Recent practice" card stays honest next to it. */
export const STATUS_DETAIL: Record<Status, string> = {
  mastered: "still right on a later day.",
  secure: "right on their own; not yet checked on a later day.",
  not_yet: "an earlier step they haven't shown on their own yet.",
  practicing: "the skill they are currently working on.",
  // MA-82 slice 3 (operator RED MUST-2): a tree can hold one Now per open
  // prerequisite chain, so this never claims to be THE current focus, nor
  // that the skills before it are done (MA-85 round 2, RED MUST-1).
  next: "ready to learn next. Each separate line of skills can have its own.",
  age_covered: "below the current level and not tried yet.",
  locked: "above where they are now; it comes after the skills before it.",
  // MA-954 (operator D-1017-22/23): name the real control, the skill page's "Bring it back".
  suppressed: "you took it out of today's mission. Tap Bring it back to add it again.",
};

/** SkillDetail's gloss: the row's word, then the status detail. */
export function progressGlossFor(status: Status, attempted: boolean, isServedNow: boolean): string {
  return `${progressWordFor(status, attempted, isServedNow)} — ${STATUS_DETAIL[status]}`;
}

/** The attempted-row gloss per Status (STATUS_CHIP_LABEL's word + detail). */
export const STATUS_CHIP_GLOSS: Record<Status, string> = Object.fromEntries(
  TREE_STATUSES.map((st) => [st, `${STATUS_CHIP_LABEL[st]} — ${STATUS_DETAIL[st]}`]),
) as Record<Status, string>;

// ── MA-297 (live-verify batch-2) — the ONE "secured set" definition ──────────
//
// "Secured" (the strand counter's set; MA-296/MA-DEC-0722 #5 (c); since
// MA-85 the counter's displayed word is "done", ADR-107) counts BOTH tiers at or above the secure bar: `secure` (≥0.70 same-day
// provisional, masteryTiers.ts) AND `mastered` (≥0.85 cross-day confirmed).
// SkillTree's strand counter has counted exactly this pair since MA-296
// ("{masteredCount}/{n} done" since MA-85) — but the pair lived only as inline status
// literals, so a second reader (the MA-297 confirm-gate live-verify hook, and
// its scenario's chip corroboration in scripts/live-qa/scenarios/
// ma297ConfirmGate.mjs) could hand-keep its own list and silently drift (the
// exact §3b batch-1 REJECT: a scenario corroborated against a phantom
// "secured" status token that no emitter ever produced). This export is the
// single home; every counter of "secured skills" — chip-side or hook-side —
// must consume it, never restate the pair.
//
// WHY BOTH TIERS (the day-2 migration argument, pinned here so nobody
// "simplifies" this to exactly-secure): statusFor above maps tier→status
// FIRST (mastered/secure win over every fold state), and a skill whose
// cross-day confirm lands tends to LEAVE the `secure` tier for `mastered`
// the same day (SkillTree.tsx's MA-296 comment; masteryTiers.ts:31-36
// score mapping). A secured-set defined as exactly-`secure` would shrink by
// one at the very moment a confirm lands, making the day-2 confirmed
// increment structurally invisible to the two-dispatch close pair. The
// {secure, mastered} set is invariant under that migration.
export const SECURED_TREE_STATUSES = Object.freeze(["mastered", "secure"] as const);

/** True for a Status in the secured set. Accepts the raw string a captured
 *  `data-skill-status` chip carries too (the scenario's corroboration side),
 *  so both readers run the SAME membership test on the SAME list. */
export function isSecuredStatus(status: Status | string): boolean {
  return (SECURED_TREE_STATUSES as readonly string[]).includes(status);
}

// ── MA-1163 (operator D-1031-31) — ONE "done" count for the parent area ─────
// The tree's strand counter and the Where-now card both show a strand's done
// count; before MA-1163 the card counted Mastered only ("Mastered: 1" beside
// the tree's "2/5 done"). Both now call this pair over the same fold rows.

/** A strand's done count: `done` is the secured set (isSecuredStatus, the
 *  ONE membership), split into its two tiers. done === mastered + almostThere. */
export interface StrandDoneCount {
  done: number;
  mastered: number;
  almostThere: number;
}

export function strandDoneCount(statuses: readonly (Status | string)[]): StrandDoneCount {
  const out: StrandDoneCount = { done: 0, mastered: 0, almostThere: 0 };
  for (const s of statuses) {
    if (!isSecuredStatus(s)) continue;
    out.done++;
    if (s === "mastered") out.mastered++;
    else out.almostThere++;
  }
  return out;
}

/** The words both surfaces show: "<n> done", plus the split
 *  "(<k> Mastered, <j> Almost there)" only when both tiers are non-zero. The
 *  tier words are the chip words (STATUS_CHIP_LABEL), never retyped. */
export function strandDoneLabel(c: StrandDoneCount): string {
  if (c.mastered > 0 && c.almostThere > 0) {
    return `${c.done} done (${c.mastered} ${STATUS_CHIP_LABEL.mastered}, ${c.almostThere} ${STATUS_CHIP_LABEL.secure})`;
  }
  return `${c.done} done`;
}

// ── MA-82 slice 3 (operator RED MUST-2) — "In mission" only on what the mission serves ──
//
// SkillTree's MA-89 removable button reads "In mission" ("Remove X from today's
// mission"), a claim MA-395(d) chose because it was TRUE: the engine "Now" was in
// today's practice. Once the fold paints one "Now" per open prerequisite chain, most
// "Now" rows are NOT in today's mission (the mission still serves one focal skill,
// useTodaysMission's `recommendedSkill`; MA-87 builds the focal SET). So the button
// is shown only on a non-assigned "Now" row the mission actually serves; every
// other "Now" row falls through to the ordinary Assign / Soon affordance.

/** The skill ids today's mission serves: its focal `recommendedSkill` plus the
 *  parent-assigned queue (buildSessionRun draws its extra slots from it). An
 *  absent or still-loading mission serves nothing — never guess. */
export function missionServedSkillIds(
  mission: { recommendedSkill?: string | null } | null | undefined,
  assignedSkillIds: readonly string[] = [],
): Set<string> {
  const ids = new Set<string>(assignedSkillIds);
  if (mission?.recommendedSkill) ids.add(mission.recommendedSkill);
  return ids;
}

/** True when a tree row shows the MA-89 "In mission" (remove-from-today's-mission)
 *  button: a "Now" row (next/practicing) the parent did NOT assign (assigned rows
 *  carry the real Assigned toggle) that today's mission actually serves. */
export function isInMissionNow(
  status: Status,
  isAssigned: boolean,
  skillId: string,
  missionSkillIds: ReadonlySet<string>,
): boolean {
  return !isAssigned && (status === "practicing" || status === "next") && missionSkillIds.has(skillId);
}

// ── MA-133 — the action-button label for a row with NO independent-practice
// pool (the skill is not in `assignablePoolIds`, so Assign can't render) ──
//
// SkillTree.tsx used to render `null` (nothing at all) for this slot whenever
// `status === "age_covered"`, because MA-90 correctly judged the generic
// "Soon" pill misleading there — "Soon" implies the content is imminent,
// but an age_covered row is already BELOW the child's current band, so its
// missing pool isn't "coming soon", it may never gain independent-practice
// content (e.g. audio-oral or handwriting-tracing skills awaiting a delivery
// mode that doesn't exist yet). Removing the misleading label without adding
// an honest replacement left the row silently blank — indistinguishable from
// a rendering bug. Every OTHER unassignable row still gets a legible "Soon"
// state; age_covered rows now get a distinct, timing-neutral one instead.
export type UnservablePoolChipLabel = "not-yet-practicable" | "soon";

/** Which label an unassignable (no-pool) row's action button should show.
 *  Callers must already know the skill has zero servable pool — this only
 *  decides WHICH of the two honest labels applies. */
export function unservablePoolChipLabel(status: Status): UnservablePoolChipLabel {
  return status === "age_covered" ? "not-yet-practicable" : "soon";
}

/** Reproduce the tree fold for ONE skill so a per-skill surface (SkillDetail)
 *  agrees with the tree BY CONSTRUCTION: filter `nodes` to the focal skill's
 *  strand, sort by ageBandRank with the tree's stable filtered-index tiebreak,
 *  run the same `computeStrandStatuses` fold, and return the focal Status.
 *  Returns null if the id isn't present. The focal Status depends on the WHOLE
 *  strand (its prerequisites' statuses, and for an above-band skill the MA-91
 *  exhaustion of everything before it in the walk), so this must not be reduced to a
 *  per-node check — that is exactly what reproduces MA-91's conditional
 *  exhaustion-lift (above-band → "next" once on-band is mastered). */
export function statusForSkillInStrand<
  T extends SkillStatusNode & { strand: string; ageBand: string },
>(
  nodes: readonly T[],
  skillId: string,
  ageCovered: Set<string>,
  aboveBand: Set<string> = new Set(),
  suppressed: Set<string> = new Set(),
): Status | null {
  const focal = nodes.find((n) => n.id === skillId);
  if (!focal) return null;
  const sorted = sortStrandByAgeBand(nodes.filter((n) => n.strand === focal.strand));
  return (
    computeStrandStatuses(sorted, ageCovered, aboveBand, suppressed).find(
      (r) => r.node.id === skillId,
    )?.status ?? null
  );
}
